import { create } from 'zustand';
import { fetchAllQuotes, fetchBreadth } from '../api/market';
import type { RealtimeQuote, MarketBreadth, PaginatedQuotes } from '../types';
import { isTradingTime } from '../utils/constants';

/**
 * 行情 Store
 * 管理：全市场行情 + 大盘状态 + 轮询控制
 */
interface MarketState {
  /** 分页行情数据 */
  quotes: RealtimeQuote[];
  /** 总数 */
  total: number;
  /** 当前页 */
  page: number;
  /** 每页大小 */
  pageSize: number;
  /** 排序字段 */
  sort: string;
  /** 排序方向 */
  order: 'asc' | 'desc';
  /** 搜索关键词 */
  search: string;
  /** 大盘状态 */
  breadth: MarketBreadth | null;
  /** 加载状态 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 轮询定时器 */
  pollTimer: number | null;
  /** 大盘状态轮询定时器 */
  breadthTimer: number | null;

  /** 获取行情数据 */
  fetchQuotes: () => Promise<void>;
  /** 设置分页 */
  setPage: (page: number) => void;
  /** 设置排序 */
  setSort: (sort: string, order?: 'asc' | 'desc') => void;
  /** 设置搜索 */
  setSearch: (search: string) => void;
  /** 获取大盘状态 */
  fetchBreadth: () => Promise<void>;
  /** 启动轮询 */
  startPolling: () => void;
  /** 停止轮询 */
  stopPolling: () => void;
}

export const useMarketStore = create<MarketState>((set, get) => ({
  quotes: [],
  total: 0,
  page: 1,
  pageSize: 50,
  sort: 'changePct',
  order: 'desc',
  search: '',
  breadth: null,
  loading: false,
  error: null,
  pollTimer: null,
  breadthTimer: null,

  fetchQuotes: async () => {
    const { page, pageSize, sort, order, search } = get();
    set({ loading: true, error: null });
    try {
      const result: PaginatedQuotes = await fetchAllQuotes(page, pageSize, sort, order, search);
      set({ quotes: result.data, total: result.total, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  },

  setPage: (page: number) => {
    set({ page });
    get().fetchQuotes();
  },

  setSort: (sort: string, order: 'asc' | 'desc' = 'desc') => {
    set({ sort, order, page: 1 });
    get().fetchQuotes();
  },

  setSearch: (search: string) => {
    set({ search, page: 1 });
    get().fetchQuotes();
  },

  fetchBreadth: async () => {
    try {
      const breadth = await fetchBreadth();
      set({ breadth });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  startPolling: () => {
    const { pollTimer, breadthTimer } = get();

    // 立即拉取一次
    get().fetchQuotes();
    get().fetchBreadth();

    // 自调度行情轮询（每次根据交易时段动态选择间隔 5s/30s）
    if (!pollTimer) {
      const schedule = () => {
        const interval = isTradingTime() ? 5000 : 30000;
        const timer = window.setTimeout(async () => {
          await get().fetchQuotes();
          schedule(); // 重新调度
        }, interval);
        set({ pollTimer: timer });
      };
      schedule();
    }

    // 自调度大盘状态轮询
    if (!breadthTimer) {
      const schedule = () => {
        const interval = isTradingTime() ? 3000 : 30000;
        const timer = window.setTimeout(async () => {
          await get().fetchBreadth();
          schedule();
        }, interval);
        set({ breadthTimer: timer });
      };
      schedule();
    }
  },

  stopPolling: () => {
    const { pollTimer, breadthTimer } = get();
    if (pollTimer) {
      clearTimeout(pollTimer);
      set({ pollTimer: null });
    }
    if (breadthTimer) {
      clearTimeout(breadthTimer);
      set({ breadthTimer: null });
    }
  },
}));
